import { useEffect, useState, useMemo } from "react";

import crossImg from "assets/img/blue-cross-icon.svg";
import { BiSearch } from "react-icons/bi";
import NoMatchFoundIcon from "assets/icons/NoMatchFoundIcon";
import { DropDownItem } from "views/pages/DataPage/CardsView/DataPageFilters/DropDownItem";
import AutoSizer from "react-virtualized-auto-sizer";
import { VariableSizeList as List } from "react-window";

import styles from "./style.module.scss";
import { DropDownItem as DropDownItemType } from "types";

interface MultiSelectDropDownProps {
  items: DropDownItemType[];
  selectedItemsIds: number[];
  onClearSelection: () => void;
  onItemSelection: (item: number) => void;
}

const MultiSelectDropDown = ({
  items,
  selectedItemsIds,
  onClearSelection,
  onItemSelection,
}: MultiSelectDropDownProps) => {
  const [searchText, setSearchText] = useState("");
  const [searchedItems, setSearchedItems] = useState<DropDownItemType[]>(items);
  const [selectedItems, nonSelectedItems] = useMemo(() => {
    return items.reduce(
      (acc, item) => {
        if (selectedItemsIds.includes(item.id)) {
          acc[0].push(item);
        } else {
          acc[1].push(item);
        }
        return acc;
      },
      [[], []] as [DropDownItemType[], DropDownItemType[]]
    );
  }, [items, selectedItemsIds]);

  const dynamicOptions =
    searchText.length > 0 ? searchedItems : nonSelectedItems;

  const handleClearSelection = (e: any) => {
    e.stopPropagation();
    onClearSelection();
    setSearchText("");
  };

  const handleCloseSearchClick = (e: any) => {
    setSearchText("");
    e.stopPropagation();
    e.preventDefault();
  };

  const showClearButton = () => {
    return (
      selectedItems.length > 0 && (
        <div
          className={styles.clearButton}
          onClick={handleClearSelection}
          data-testid="multi-select-dropdown-clear-button"
        >
          Clear Selection
        </div>
      )
    );
  };

  useEffect(() => {
    if (searchText === "") {
      return;
    }
    setSearchedItems(
      nonSelectedItems.filter((option: any) => {
        return (
          option.text?.toLowerCase().includes(searchText) &&
          !selectedItemsIds.includes(option.id)
        );
      })
    );
    //eslint-disable-next-line
  }, [searchText, selectedItemsIds]);

  const calculateOptionsContainerStyle = () => {
    let contentHeight = "";
    const optionsCount = dynamicOptions.length;
    const highestValue = dynamicOptions.reduce(
      (maxText: string, currentItem: DropDownItemType) => {
        if (currentItem.text.length > maxText.length) {
          return currentItem.text;
        } else {
          return maxText;
        }
      },
      ""
    );

    if (optionsCount > 0 && optionsCount <= 5) {
      contentHeight = optionsCount * 40 + "px";
    } else {
      contentHeight = "200px";
    }
    return {
      height: contentHeight,
      minWidth: "100%",
      width: `calc((${highestValue.length} * 0.45em) + 56px)`,
    };
  };

  return (
    <div className={styles.dropdown} data-testid="multi-select-dropdown">
      <div className={styles.selectedOptionsContainer}>
        <div className={styles.searchBarContainer}>
          {!searchText && (
            <div className={styles.searchBarButton}>
              <BiSearch />
            </div>
          )}
          <input
            type="search"
            onChange={(e: any) => setSearchText(e.target.value?.toLowerCase())}
            value={searchText}
            className={styles.searchBarInput}
            data-testid="multi-select-dropdown-search-input"
          />
          {searchText && (
            <img
              className={styles.crossImg}
              src={crossImg}
              alt="cross-icon"
              onClick={handleCloseSearchClick}
              data-testid="multi-select-dropdown-clear-icon"
            />
          )}
        </div>
        <div
          className={styles.selectedItems}
          data-testid="multi-select-dropdown-selected-items"
        >
          {selectedItems.map((item: DropDownItemType, index: number) => {
            return (
              <DropDownItem
                key={index}
                item={item}
                isSelected={true}
                onSelect={onItemSelection}
              />
            );
          })}
        </div>
      </div>
      {showClearButton()}
      <div className={styles.horizintalRule}></div>
      {dynamicOptions.length > 0 && (
        <div
          className={styles.nonSelectedItems}
          style={calculateOptionsContainerStyle()}
          data-testid="multi-select-dropdown-non-selected-items"
        >
          <AutoSizer>
            {({ height, width }: any) => (
              <List
                height={height}
                width={width}
                itemCount={dynamicOptions.length}
                itemSize={(index: number) => 40}
              >
                {({ index, style }) => (
                  <div style={style}>
                    <DropDownItem
                      key={index}
                      item={dynamicOptions[index]}
                      isSelected={false}
                      onSelect={onItemSelection}
                    />
                  </div>
                )}
              </List>
            )}
          </AutoSizer>
        </div>
      )}
      {!!searchText && dynamicOptions.length <= 0 && (
        <div
          className={styles.noSearchDataBox}
          data-testid="multi-select-dropdown-no-matches"
        >
          <NoMatchFoundIcon />
          <span className={styles.noSearchDataText}>No matches found</span>
          <span className={styles.noSearchDataText}>
            Please try another term
          </span>
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropDown;
